import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DashboardTile as DashboardTileType } from '../../api/hooks/useDashboard';
import { TileHeader, TileContent } from './tiles';
import { useTelemetry } from '../../telemetry/useTelemetry';

interface DashboardTileProps {
  tile: DashboardTileType;
  className?: string;
  isDragDisabled?: boolean;
}

const DashboardTile: React.FC<DashboardTileProps> = ({
  tile,
  className = '',
  isDragDisabled = false
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: tile.tileId,
    disabled: isDragDisabled
  });

  const { trackEvent } = useTelemetry();

  const style = isDragDisabled ? {} : {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const getSizeClasses = () => {
    // On mobile (<768px), all tiles are full-width (col-span-1)
    // On desktop (>=768px), tiles use their configured size
    switch (tile.size) {
      case 'Small':
        return 'col-span-1 row-span-1';
      case 'Medium':
        return 'col-span-1 md:col-span-2 row-span-1';
      case 'Large':
        return 'col-span-1 md:col-span-2 row-span-1 md:row-span-2';
      default:
        return 'col-span-1 row-span-1';
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`
        bg-white dark:bg-graphite-surface rounded-lg shadow-sm dark:shadow-soft-dark border border-gray-200 dark:border-graphite-border
        hover:shadow-md transition-shadow duration-200
        flex flex-col
        ${getSizeClasses()}
        ${className}
      `}
      data-testid={`dashboard-tile-${tile.tileId}`}
      onClick={() => { if (!isDragging) trackEvent('DashboardTileClicked', { tileId: tile.tileId }); }}
    >
      <TileHeader
        title={tile.title}
        dragHandleProps={isDragDisabled ? undefined : { ...attributes, ...listeners }}
      />

      <div className="p-4 flex-1">
        <TileContent tile={tile} />
      </div>
    </div>
  );
};

export default DashboardTile;
